import { useCallback, useRef, useState } from "react";
import { usePagePrefs } from "@pepsico/pref-permissions";
import { isEqual } from "es-toolkit";

import type { RowGeoResponse } from "api/model";
import { useGetSitesGeoByLocation } from "api/sites-geo/sites-geo";

import {
  boundaryHistoryReducer,
  canRedoBoundaryHistory,
  canUndoBoundaryHistory,
  type BoundaryHistoryState,
} from "./boundaryHistory";
import {
  getBoundaryCenter,
  MIN_BOUNDARY_POINTS_TO_CLOSE,
} from "./boundaryUtils";
import { getBoundsFromCoordinates, type BoundaryPoint } from "./geo";
import {
  canRedoGridHistory,
  canUndoGridHistory,
  gridHistoryReducer,
  hasRecordableGridHistory,
  type GridHistorySnapshot,
  type GridHistoryState,
} from "./gridMeshHistory";
import {
  finalizeEdgeChainSlide,
  finalizeSingleEdgeSlide,
  slideDetachedEdgeChain,
  slideSingleEdge,
  slideVertex,
} from "./gridMeshSlide";
import {
  commitLatticeToMesh,
  deleteCell,
  deleteEdge,
  deleteVertex,
  detachIsland,
  insertVertexOnEdge,
  isMergeable,
  mergeCells,
  selectionSharesExternalVertices,
} from "./gridMeshTopology";
import {
  cellRingVertexOrigins,
  moveEdge,
  moveEdgeChain,
  moveVertex,
  rotateCellsFromOrigins,
  translateCells,
} from "./gridMeshTransform";
import type {
  FrozenEdgeSlideConstraint,
  FrozenVertexSlideConstraint,
  GridCellSeed,
  GridEdgeRef,
  GridEdgeSlideResult,
  GridMesh,
} from "./gridMeshTypes";
import { splitMeshPlotsIntoRows } from "./gridRowSplit";
import {
  createPlotGrid,
  createPlotSizedGridTransform,
  DEFAULT_PLOT_LENGTH_METERS,
  DEFAULT_PLOT_WIDTH_METERS,
  plotSizeFromGridTransform,
  type GridTransform,
  type PlotGrid,
} from "./gridUtils";
import { PlotMapContext } from "./hooks";
import {
  EMPTY_MESH_OVERLAP,
  updateMeshOverlap,
  type MeshOverlapState,
} from "./plotGridGeoUtils";

const parseCoordPref = (
  value: string | string[] | undefined,
): number | null => {
  if (typeof value !== "string" || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clonePlotGrid = (grid: PlotGrid | null): PlotGrid | null => {
  if (grid == null) {
    return null;
  }

  return structuredClone(grid);
};

const updateCommittedMesh = (
  currentGrid: PlotGrid | null,
  update: (mesh: GridMesh) => GridMesh | null,
): PlotGrid | null => {
  if (
    currentGrid == null ||
    !currentGrid.isCommitted ||
    currentGrid.mesh == null
  ) {
    return currentGrid;
  }

  const nextMesh = update(currentGrid.mesh);
  if (nextMesh == null) {
    return currentGrid;
  }

  return {
    ...currentGrid,
    mesh: nextMesh,
  };
};

interface GridSummary {
  cells: number;
  committed: boolean;
  persistedCells: number;
  rows: number;
  vertices: number;
}

// High-level counts for tracing plots/rows through grid edits.
const summarizeGrid = (grid: PlotGrid | null): GridSummary | null => {
  if (grid == null) {
    return null;
  }

  const mesh = grid.mesh;
  return {
    cells: mesh?.cells.length ?? 0,
    committed: grid.isCommitted,
    persistedCells:
      mesh?.cells.filter((cell) => cell.plotGeoId != null).length ?? 0,
    rows: mesh?.rows?.length ?? 0,
    vertices: mesh == null ? 0 : Object.keys(mesh.vertices).length,
  };
};

interface LatticeSummary {
  columns: number | null;
  plotLengthMeters: number | null;
  plotWidthMeters: number | null;
  points: number;
  rows: number | null;
}

// Pre-commit lattice shape; the mesh summary is empty until commitGrid.
const summarizeLattice = (grid: PlotGrid | null): LatticeSummary | null =>
  grid == null
    ? null
    : {
        columns: grid.columns,
        plotLengthMeters: grid.plotLengthMeters,
        plotWidthMeters: grid.plotWidthMeters,
        points: grid.points.length,
        rows: grid.rows,
      };

const logGridOperation = (
  operation: string,
  details: Record<string, unknown>,
): void => {
  console.info(`[PlotMapProvider] ${operation}`, JSON.stringify(details));
};

interface SavedBoundarySnapshot {
  isClosed: boolean;
  label: string;
  points: BoundaryPoint[];
}

const EMPTY_SAVED_BOUNDARY: SavedBoundarySnapshot = {
  isClosed: false,
  label: "",
  points: [],
};

const cloneBoundaryPoints = (points: BoundaryPoint[]): BoundaryPoint[] =>
  points.map((point) => ({
    latitude: point.latitude,
    longitude: point.longitude,
  }));

const createSavedBoundarySnapshot = (
  points: BoundaryPoint[],
  isClosed: boolean,
  label: string,
): SavedBoundarySnapshot => ({
  isClosed,
  label: label.trim(),
  points: cloneBoundaryPoints(points),
});

interface PlotMapProviderProps {
  children: React.ReactNode;
}

const PlotMapProvider: React.FC<PlotMapProviderProps> = ({ children }) => {
  const [prefs] = usePagePrefs();

  // --- Boundary state ---
  const [boundaryHistory, setBoundaryHistory] =
    useState<BoundaryHistoryState | null>(null);
  const [boundaryLabel, setBoundaryLabel] = useState("");
  const [boundaryPoints, setBoundaryPoints] = useState<BoundaryPoint[]>([]);
  const [isBoundaryClosed, setIsBoundaryClosed] = useState(false);
  const [editingGrowerGeoId, setEditingGrowerGeoId] = useState<number | null>(
    null,
  );
  const [editingSiteGeoId, setEditingSiteGeoId] = useState<number | null>(null);
  const [isGrowerBoundaryMode, setGrowerBoundaryMode] = useState(false);
  const [isSiteBoundaryMode, setSiteBoundaryMode] = useState(false);
  const [savedBoundary, setSavedBoundary] =
    useState<SavedBoundarySnapshot>(EMPTY_SAVED_BOUNDARY);

  // --- Plot state (working grid + grower → site → trial it belongs to) ---
  const [grid, setGrid] = useState<PlotGrid | null>(null);
  const [gridHistory, setGridHistory] = useState<GridHistoryState | null>(null);
  // Empty until an edit gesture ends. Commit and saved geometry are already valid.
  const [meshOverlap, setMeshOverlap] =
    useState<MeshOverlapState>(EMPTY_MESH_OVERLAP);
  const [isPlotGridMode, setPlotGridMode] = useState(false);
  const [savedGrid, setSavedGrid] = useState<PlotGrid | null>(null);
  const [selectedCellIds, setSelectedCellIds] = useState<string[]>([]);
  const [selectedVertexId, setSelectedVertexId] = useState<string | null>(null);

  // Ref-only flag: gesture begin/end must toggle synchronously without a render.
  const gestureActiveRef = useRef(false);
  // Mode ref so enterPlotGridMode() + setDimensions() in the same tick works.
  const isPlotGridModeRef = useRef(false);
  // Ref + state for grid history / working grid / selection serve different paths:
  // - Ref: begin/end gesture, merge, undo/redo read/write the latest values
  //   synchronously (including updates applied inside setState updaters) without
  //   waiting on a render or closing over stale provider state.
  // - State: re-render for map/panel UI (canUndo/canRedo, mesh, selection).
  const boundaryHistoryRef = useRef(boundaryHistory);
  boundaryHistoryRef.current = boundaryHistory;
  const boundaryPointsRef = useRef(boundaryPoints);
  boundaryPointsRef.current = boundaryPoints;
  const isBoundaryClosedRef = useRef(isBoundaryClosed);
  isBoundaryClosedRef.current = isBoundaryClosed;
  const gridHistoryRef = useRef(gridHistory);
  gridHistoryRef.current = gridHistory;
  const gridRef = useRef(grid);
  gridRef.current = grid;
  const meshOverlapRef = useRef(meshOverlap);
  meshOverlapRef.current = meshOverlap;
  const selectedCellIdsRef = useRef(selectedCellIds);
  selectedCellIdsRef.current = selectedCellIds;

  const [selectedGrowerGeoId, setSelectedGrowerGeoId] = useState<number | null>(
    null,
  );
  const [selectedSiteGeoId, setSelectedSiteGeoId] = useState<number | null>(
    null,
  );
  const [selectedTrialId, setSelectedTrialId] = useState<number | null>(null);

  const locationUuid = (prefs.location as string | null | undefined) ?? "";

  const { data: sitesGeo = [] } = useGetSitesGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res) => (res.status === 200 ? res.data : []),
      },
    },
  );

  // --- Boundary ---

  const clearBoundaryHistory = useCallback(() => {
    boundaryHistoryRef.current = null;
    setBoundaryHistory(null);
  }, []);

  const commitBoundaryHistoryGesture = useCallback(() => {
    const history = boundaryHistoryRef.current;
    if (history == null) {
      return;
    }

    const nextState = boundaryHistoryReducer(history, {
      snapshot: {
        isClosed: isBoundaryClosedRef.current,
        points: boundaryPointsRef.current,
      },
      type: "commitGesture",
    });
    boundaryHistoryRef.current = nextState;
    setBoundaryHistory(nextState);
  }, []);

  const setBoundaryTo = useCallback(
    (snapshot: { isClosed: boolean; points: BoundaryPoint[] }) => {
      setBoundaryPoints(snapshot.points);
      boundaryPointsRef.current = snapshot.points;
      setIsBoundaryClosed(snapshot.isClosed);
      isBoundaryClosedRef.current = snapshot.isClosed;
    },
    [],
  );

  const seedBoundaryHistory = useCallback(() => {
    const seeded = boundaryHistoryReducer(null, {
      snapshot: {
        isClosed: isBoundaryClosedRef.current,
        points: boundaryPointsRef.current,
      },
      type: "seed",
    });
    boundaryHistoryRef.current = seeded;
    setBoundaryHistory(seeded);
  }, []);

  // Dirty when the working draft differs from the last known server snapshot.
  const isBoundaryDirty =
    isBoundaryClosed !== savedBoundary.isClosed ||
    boundaryLabel.trim() !== savedBoundary.label ||
    !isEqual(boundaryPoints, savedBoundary.points);

  const isBoundaryDrawMode = isGrowerBoundaryMode || isSiteBoundaryMode;

  const enterGrowerBoundaryMode = useCallback(
    (growerGeoId: number | null) => {
      if (boundaryHistoryRef.current == null) {
        seedBoundaryHistory();
      }
      setEditingGrowerGeoId(growerGeoId);
      setEditingSiteGeoId(null);
      setSiteBoundaryMode(false);
      setGrowerBoundaryMode(true);
    },
    [seedBoundaryHistory],
  );

  const enterSiteBoundaryMode = useCallback(
    (siteGeoId: number) => {
      if (boundaryHistoryRef.current == null) {
        seedBoundaryHistory();
      }
      setEditingGrowerGeoId(null);
      setEditingSiteGeoId(siteGeoId);
      setGrowerBoundaryMode(false);
      setSiteBoundaryMode(true);
    },
    [seedBoundaryHistory],
  );

  const pauseGrowerBoundaryMode = useCallback(() => {
    setGrowerBoundaryMode(false);
    setSiteBoundaryMode(false);
    // Drop undirtied timelines so a later enter re-seeds from current geometry.
    if (!isBoundaryDirty) {
      clearBoundaryHistory();
    }
  }, [clearBoundaryHistory, isBoundaryDirty]);

  const applySnapshotToWorkingState = useCallback(
    (snapshot: SavedBoundarySnapshot) => {
      const points = cloneBoundaryPoints(snapshot.points);
      setBoundaryPoints(points);
      boundaryPointsRef.current = points;
      setIsBoundaryClosed(snapshot.isClosed);
      isBoundaryClosedRef.current = snapshot.isClosed;
      setBoundaryLabel(snapshot.label);
    },
    [],
  );

  const endBoundarySession = useCallback(() => {
    clearBoundaryHistory();
    setEditingGrowerGeoId(null);
    setEditingSiteGeoId(null);
    setGrowerBoundaryMode(false);
    setSiteBoundaryMode(false);
  }, [clearBoundaryHistory]);

  const doesWorkingStateMatchSnapshot = useCallback(
    (snapshot: SavedBoundarySnapshot) =>
      isBoundaryClosed === snapshot.isClosed &&
      boundaryLabel === snapshot.label &&
      isEqual(boundaryPoints, snapshot.points),
    [boundaryLabel, boundaryPoints, isBoundaryClosed],
  );

  // Commit the current draft as the new server baseline (after a successful save).
  const acceptSavedBoundary = useCallback(() => {
    const label = boundaryLabel.trim();
    setBoundaryLabel(label);
    setSavedBoundary(
      createSavedBoundarySnapshot(
        boundaryPointsRef.current,
        isBoundaryClosedRef.current,
        label,
      ),
    );
    endBoundarySession();
  }, [boundaryLabel, endBoundarySession]);

  // Hydrate working state + server baseline from saved geometry.
  const loadSavedBoundary = useCallback(
    (points: BoundaryPoint[], label = "") => {
      const snapshot = createSavedBoundarySnapshot(
        points,
        points.length > 0,
        label,
      );
      const alreadyLoaded =
        !isBoundaryDrawMode &&
        boundaryHistoryRef.current == null &&
        doesWorkingStateMatchSnapshot(snapshot) &&
        isEqual(savedBoundary, snapshot);
      if (alreadyLoaded) {
        return;
      }

      applySnapshotToWorkingState(snapshot);
      setSavedBoundary(snapshot);
      endBoundarySession();
    },
    [
      applySnapshotToWorkingState,
      endBoundarySession,
      isBoundaryDrawMode,
      doesWorkingStateMatchSnapshot,
      savedBoundary,
    ],
  );

  const addBoundaryPoint = useCallback(
    (point: BoundaryPoint) => {
      if (!isBoundaryDrawMode || isBoundaryClosedRef.current) {
        return;
      }

      setBoundaryPoints((currentPoints) => {
        const nextPoints = [...currentPoints, point];
        boundaryPointsRef.current = nextPoints;
        return nextPoints;
      });
      commitBoundaryHistoryGesture();
    },
    [commitBoundaryHistoryGesture, isBoundaryDrawMode],
  );

  // Abandon the draft: restore working state from the server baseline.
  const cancelBoundaryDraft = useCallback(() => {
    const alreadyRestored =
      !isBoundaryDrawMode &&
      boundaryHistoryRef.current == null &&
      doesWorkingStateMatchSnapshot(savedBoundary);
    if (alreadyRestored) {
      return;
    }

    applySnapshotToWorkingState(savedBoundary);
    endBoundarySession();
  }, [
    applySnapshotToWorkingState,
    endBoundarySession,
    isBoundaryDrawMode,
    doesWorkingStateMatchSnapshot,
    savedBoundary,
  ]);

  const clearBoundary = useCallback(() => {
    if (!isBoundaryDrawMode) {
      return;
    }

    setBoundaryPoints([]);
    boundaryPointsRef.current = [];
    setIsBoundaryClosed(false);
    isBoundaryClosedRef.current = false;
    commitBoundaryHistoryGesture();
  }, [commitBoundaryHistoryGesture, isBoundaryDrawMode]);

  const closeBoundary = useCallback(() => {
    if (
      !isBoundaryDrawMode ||
      isBoundaryClosedRef.current ||
      boundaryPointsRef.current.length < MIN_BOUNDARY_POINTS_TO_CLOSE
    ) {
      return;
    }

    setIsBoundaryClosed(true);
    isBoundaryClosedRef.current = true;
    commitBoundaryHistoryGesture();
  }, [commitBoundaryHistoryGesture, isBoundaryDrawMode]);

  const insertBoundaryPoint = useCallback(
    (index: number, point: BoundaryPoint) => {
      if (!isBoundaryDrawMode || !isBoundaryClosedRef.current) {
        return;
      }

      setBoundaryPoints((currentPoints) => {
        if (index < 0 || index > currentPoints.length) {
          return currentPoints;
        }

        const nextPoints = [
          ...currentPoints.slice(0, index),
          point,
          ...currentPoints.slice(index),
        ];
        boundaryPointsRef.current = nextPoints;
        return nextPoints;
      });
      commitBoundaryHistoryGesture();
    },
    [commitBoundaryHistoryGesture, isBoundaryDrawMode],
  );

  const redoBoundary = useCallback(() => {
    const history = boundaryHistoryRef.current;
    if (!canRedoBoundaryHistory(history)) {
      return;
    }

    const nextState = boundaryHistoryReducer(history, {
      snapshot: {
        isClosed: isBoundaryClosedRef.current,
        points: boundaryPointsRef.current,
      },
      type: "redo",
    });
    if (nextState == null) {
      return;
    }

    boundaryHistoryRef.current = nextState;
    setBoundaryHistory(nextState);
    setBoundaryTo(nextState.entries[nextState.index]);
  }, [setBoundaryTo]);

  const removeBoundaryPoint = useCallback(
    (index: number) => {
      if (!isBoundaryDrawMode) {
        return;
      }

      const currentPoints = boundaryPointsRef.current;
      if (index < 0 || index >= currentPoints.length) {
        return;
      }

      const nextPoints = currentPoints.filter(
        (_, pointIndex) => pointIndex !== index,
      );
      setBoundaryPoints(nextPoints);
      boundaryPointsRef.current = nextPoints;

      if (nextPoints.length < MIN_BOUNDARY_POINTS_TO_CLOSE) {
        setIsBoundaryClosed(false);
        isBoundaryClosedRef.current = false;
      }
      commitBoundaryHistoryGesture();
    },
    [commitBoundaryHistoryGesture, isBoundaryDrawMode],
  );

  const undoBoundary = useCallback(() => {
    const history = boundaryHistoryRef.current;
    if (!canUndoBoundaryHistory(history)) {
      return;
    }

    const nextState = boundaryHistoryReducer(history, {
      snapshot: {
        isClosed: isBoundaryClosedRef.current,
        points: boundaryPointsRef.current,
      },
      type: "undo",
    });
    if (nextState == null) {
      return;
    }

    boundaryHistoryRef.current = nextState;
    setBoundaryHistory(nextState);
    setBoundaryTo(nextState.entries[nextState.index]);
  }, [setBoundaryTo]);

  const updateBoundaryPoint = useCallback(
    (index: number, point: BoundaryPoint) => {
      if (!isBoundaryDrawMode) {
        return;
      }

      setBoundaryPoints((currentPoints) => {
        if (index < 0 || index >= currentPoints.length) {
          return currentPoints;
        }

        const nextPoints = currentPoints.map((currentPoint, pointIndex) =>
          pointIndex === index ? point : currentPoint,
        );
        boundaryPointsRef.current = nextPoints;
        return nextPoints;
      });
    },
    [isBoundaryDrawMode],
  );

  // --- Plot ---

  const getFreeGridCenter = useCallback((): BoundaryPoint | null => {
    // Prefer the selected site so a new lattice opens over its trial area.
    const selectedSite = sitesGeo.find((site) => site.id === selectedSiteGeoId);
    const siteCenter = getBoundaryCenter(selectedSite?.boundary);
    if (siteCenter != null) {
      return siteCenter;
    }

    // Fall back to the grower-boundary bbox, then the map view prefs.
    const boundaryBounds = getBoundsFromCoordinates(savedBoundary.points);
    if (boundaryBounds != null) {
      const [[minLng, minLat], [maxLng, maxLat]] = boundaryBounds;
      return {
        latitude: (minLat + maxLat) / 2,
        longitude: (minLng + maxLng) / 2,
      };
    }

    const latitude = parseCoordPref(prefs.lat);
    const longitude = parseCoordPref(prefs.lng);
    if (latitude == null || longitude == null) {
      return null;
    }

    return { latitude, longitude };
  }, [prefs.lat, prefs.lng, savedBoundary.points, selectedSiteGeoId, sitesGeo]);

  const clearMeshOverlap = useCallback(() => {
    if (meshOverlapRef.current === EMPTY_MESH_OVERLAP) {
      return;
    }
    meshOverlapRef.current = EMPTY_MESH_OVERLAP;
    setMeshOverlap(EMPTY_MESH_OVERLAP);
  }, []);

  const syncOverlapToMeshChange = useCallback(
    (before: GridMesh | null, after: GridMesh | null) => {
      if (before == null || after == null) {
        if (after == null) {
          clearMeshOverlap();
        }
        return;
      }

      const next = updateMeshOverlap(meshOverlapRef.current, before, after);
      if (next === meshOverlapRef.current) {
        return;
      }
      meshOverlapRef.current = next;
      setMeshOverlap(next);
    },
    [clearMeshOverlap],
  );

  const clearGridHistory = useCallback(() => {
    gestureActiveRef.current = false;
    gridHistoryRef.current = null;
    setGridHistory(null);
    clearMeshOverlap();
  }, [clearMeshOverlap]);

  const clearGridSelection = useCallback(() => {
    setSelectedCellIds([]);
    selectedCellIdsRef.current = [];
    setSelectedVertexId(null);
  }, []);

  // Dirty when the working draft differs from the last known server snapshot.
  const isGridDirty = !isEqual(grid, savedGrid);

  const seedGridHistoryFromCurrent = useCallback(() => {
    const currentGrid = gridRef.current;
    if (currentGrid == null || !hasRecordableGridHistory(currentGrid)) {
      return;
    }

    const seeded = gridHistoryReducer(null, {
      snapshot: { grid: currentGrid, selectedCellIds: [] },
      type: "seed",
    });
    gridHistoryRef.current = seeded;
    setGridHistory(seeded);
  }, []);

  const enterPlotGridMode = useCallback(() => {
    if (gridHistoryRef.current == null) {
      seedGridHistoryFromCurrent();
    }
    isPlotGridModeRef.current = true;
    setPlotGridMode(true);
  }, [seedGridHistoryFromCurrent]);

  const pausePlotGridMode = useCallback(() => {
    isPlotGridModeRef.current = false;
    setPlotGridMode(false);
    // Drop undirtied timelines so a later enter re-seeds from current geometry.
    if (!isGridDirty) {
      clearGridHistory();
    }
  }, [clearGridHistory, isGridDirty]);

  const endPlotGridSession = useCallback(() => {
    clearGridHistory();
    isPlotGridModeRef.current = false;
    setPlotGridMode(false);
  }, [clearGridHistory]);

  const applyGridToWorkingState = useCallback(
    (nextGrid: PlotGrid | null) => {
      const cloned = clonePlotGrid(nextGrid);
      gridRef.current = cloned;
      setGrid(cloned);
      clearGridSelection();
    },
    [clearGridSelection],
  );

  // Commit the current draft as the new server baseline (after a successful save).
  const acceptSavedGrid = useCallback(() => {
    logGridOperation("acceptSavedGrid", {
      grid: summarizeGrid(gridRef.current),
    });
    setSavedGrid(clonePlotGrid(gridRef.current));
    endPlotGridSession();
  }, [endPlotGridSession]);

  // Hydrate working state + server baseline from the plots-geo query.
  const loadSavedGrid = useCallback(
    (nextGrid: PlotGrid | null) => {
      const alreadyLoaded =
        !isPlotGridMode &&
        gridHistoryRef.current == null &&
        isEqual(grid, nextGrid) &&
        isEqual(savedGrid, nextGrid);
      if (alreadyLoaded) {
        return;
      }

      logGridOperation("loadSavedGrid", {
        current: summarizeGrid(grid),
        loaded: summarizeGrid(nextGrid),
      });
      applyGridToWorkingState(nextGrid);
      setSavedGrid(clonePlotGrid(nextGrid));
      endPlotGridSession();
    },
    [
      applyGridToWorkingState,
      endPlotGridSession,
      grid,
      isPlotGridMode,
      savedGrid,
    ],
  );

  // Abandon the draft: restore working state from the server baseline.
  const cancelGridDraft = useCallback(() => {
    const alreadyRestored =
      !isPlotGridMode &&
      gridHistoryRef.current == null &&
      isEqual(grid, savedGrid);
    if (alreadyRestored) {
      return;
    }

    logGridOperation("cancelGridDraft", {
      discarded: summarizeGrid(grid),
      restored: summarizeGrid(savedGrid),
    });
    applyGridToWorkingState(savedGrid);
    endPlotGridSession();
  }, [
    applyGridToWorkingState,
    endPlotGridSession,
    grid,
    isPlotGridMode,
    savedGrid,
  ]);

  /** Mid-gesture mesh mutation; caller owns begin/end history gestures. */
  const applyGestureMeshUpdate = useCallback(
    (update: (mesh: GridMesh) => GridMesh | null) => {
      if (!isPlotGridModeRef.current) {
        return;
      }

      const nextGrid = updateCommittedMesh(gridRef.current, update);
      gridRef.current = nextGrid;
      setGrid(nextGrid);
    },
    [],
  );

  const beginGridHistoryGesture = useCallback(() => {
    if (!isPlotGridModeRef.current || gestureActiveRef.current) {
      return;
    }

    const currentGrid = gridRef.current;
    const history = gridHistoryRef.current;
    if (
      currentGrid == null ||
      !hasRecordableGridHistory(currentGrid) ||
      history == null
    ) {
      return;
    }

    // Capture selection on the current checkpoint; geometry already matches.
    const nextState = gridHistoryReducer(history, {
      snapshot: {
        grid: currentGrid,
        selectedCellIds: selectedCellIdsRef.current,
      },
      type: "syncCheckpoint",
    });
    gridHistoryRef.current = nextState;
    setGridHistory(nextState);
    gestureActiveRef.current = true;
  }, []);

  const endGridHistoryGesture = useCallback(() => {
    if (!gestureActiveRef.current) {
      return;
    }
    gestureActiveRef.current = false;

    const history = gridHistoryRef.current;
    const beforeMesh = history?.entries[history.index]?.grid.mesh ?? null;
    const nextState = gridHistoryReducer(history, {
      snapshot:
        gridRef.current == null
          ? null
          : {
              grid: gridRef.current,
              selectedCellIds: selectedCellIdsRef.current,
            },
      type: "commitGesture",
    });
    gridHistoryRef.current = nextState;
    setGridHistory(nextState);
    logGridOperation("endGesture", {
      after: summarizeGrid(gridRef.current),
      before: summarizeGrid(history?.entries[history.index]?.grid ?? null),
      committed: nextState !== history,
      historyIndex: nextState?.index ?? null,
      historySize: nextState?.entries.length ?? null,
    });
    // No-op gestures (pointer down/up without a geometry change) keep the
    // previous overlap result. Mid-drag frames never reach this.
    if (nextState === history) {
      return;
    }
    syncOverlapToMeshChange(beforeMesh, gridRef.current?.mesh ?? null);
  }, [syncOverlapToMeshChange]);

  // The first write of a new lattice becomes the undo baseline. Later writes,
  // including commit, are normal gestures. Drags call begin/end themselves.
  const recordDiscreteLatticeEdit = useCallback(
    (mutate: () => void) => {
      if (gridHistoryRef.current == null) {
        mutate();
        seedGridHistoryFromCurrent();
        return;
      }

      beginGridHistoryGesture();
      mutate();
      endGridHistoryGesture();
    },
    [
      beginGridHistoryGesture,
      endGridHistoryGesture,
      seedGridHistoryFromCurrent,
    ],
  );

  const syncSelectionToMesh = useCallback((mesh: GridMesh) => {
    const remainingCellIds = new Set(mesh.cells.map((cell) => cell.id));
    setSelectedCellIds((current) => {
      const next = current.filter((cellId) => remainingCellIds.has(cellId));
      selectedCellIdsRef.current = next;
      return next;
    });
    setSelectedVertexId((current) =>
      current != null && mesh.vertices[current] == null ? null : current,
    );
  }, []);

  const commitGrid = useCallback(
    (cellSeeds?: Record<string, GridCellSeed>, rows?: RowGeoResponse[]) => {
      if (!isPlotGridModeRef.current) {
        return;
      }

      const currentGrid = gridRef.current;
      if (
        currentGrid == null ||
        currentGrid.isCommitted ||
        currentGrid.rows == null ||
        currentGrid.columns == null
      ) {
        return;
      }

      const nextGrid: PlotGrid = {
        ...currentGrid,
        isCommitted: true,
        mesh: splitMeshPlotsIntoRows(
          commitLatticeToMesh(
            currentGrid.rows,
            currentGrid.columns,
            currentGrid.points,
            {
              cellSeeds: cellSeeds ?? currentGrid.cellSeeds,
            },
          ),
          rows ?? [],
        ),
        points: [],
      };

      logGridOperation("commitGrid", {
        lattice: summarizeLattice(currentGrid),
        committed: summarizeGrid(nextGrid),
        rowRecords: rows?.length ?? 0,
      });
      beginGridHistoryGesture();
      gridRef.current = nextGrid;
      setGrid(nextGrid);
      clearGridSelection();
      endGridHistoryGesture();
      // A new mesh does not overlap. Undo back to the lattice clears this
      // because that snapshot has no mesh.
      clearMeshOverlap();
    },
    [
      beginGridHistoryGesture,
      clearGridSelection,
      clearMeshOverlap,
      endGridHistoryGesture,
    ],
  );

  /**
   * One-shot committed-mesh edit with its own undo gesture (delete, label
   * rename, etc.). Continuous drags use begin/end + applyGestureMeshUpdate.
   */
  const applyDiscreteMeshEdit = useCallback(
    (update: (mesh: GridMesh) => GridMesh | null): boolean => {
      if (!isPlotGridModeRef.current) {
        return false;
      }

      const currentGrid = gridRef.current;
      if (
        currentGrid == null ||
        !currentGrid.isCommitted ||
        currentGrid.mesh == null
      ) {
        return false;
      }

      beginGridHistoryGesture();
      const nextMesh = update(currentGrid.mesh);
      if (nextMesh == null) {
        endGridHistoryGesture();
        return false;
      }

      const nextGrid: PlotGrid = {
        ...currentGrid,
        mesh: nextMesh,
      };
      logGridOperation("discreteMeshEdit", {
        after: summarizeGrid(nextGrid),
        before: summarizeGrid(currentGrid),
      });
      gridRef.current = nextGrid;
      setGrid(nextGrid);
      syncSelectionToMesh(nextMesh);
      endGridHistoryGesture();
      return true;
    },
    [beginGridHistoryGesture, endGridHistoryGesture, syncSelectionToMesh],
  );

  const deleteGridCell = useCallback(
    (cellId: string) => {
      applyDiscreteMeshEdit((mesh) => deleteCell(mesh, cellId));
    },
    [applyDiscreteMeshEdit],
  );

  const deleteGridEdge = useCallback(
    (edge: GridEdgeRef) => {
      applyDiscreteMeshEdit((mesh) => deleteEdge(mesh, edge));
    },
    [applyDiscreteMeshEdit],
  );

  const deleteGridVertex = useCallback(
    (vertexId: string) => {
      applyDiscreteMeshEdit((mesh) => deleteVertex(mesh, vertexId));
    },
    [applyDiscreteMeshEdit],
  );

  // Plot grid click-on-edge insert is disabled; kept for a possible future restore
  // (grower/site boundaries still insert via useBoundaryMapInteraction).
  const insertGridVertexOnEdge = useCallback(
    (edge: GridEdgeRef, point: BoundaryPoint) => {
      if (!isPlotGridModeRef.current) {
        return;
      }

      const currentGrid = gridRef.current;
      if (
        currentGrid == null ||
        !currentGrid.isCommitted ||
        currentGrid.mesh == null
      ) {
        return;
      }

      beginGridHistoryGesture();
      const inserted = insertVertexOnEdge(currentGrid.mesh, edge, point);
      if (inserted == null) {
        endGridHistoryGesture();
        return;
      }

      const nextGrid: PlotGrid = {
        ...currentGrid,
        mesh: inserted.mesh,
      };
      gridRef.current = nextGrid;
      setGrid(nextGrid);
      syncSelectionToMesh(inserted.mesh);
      setSelectedVertexId(inserted.vertexId);
      endGridHistoryGesture();
    },
    [beginGridHistoryGesture, endGridHistoryGesture, syncSelectionToMesh],
  );

  const detachAndTranslateSelection = useCallback(
    (cellIds: string[], delta: { latitude: number; longitude: number }) => {
      applyGestureMeshUpdate((mesh) => {
        if (cellIds.length === 0) {
          return null;
        }

        let nextMesh = mesh;
        if (selectionSharesExternalVertices(nextMesh, cellIds)) {
          nextMesh = detachIsland(nextMesh, cellIds);
        }

        return translateCells(nextMesh, cellIds, delta);
      });
    },
    [applyGestureMeshUpdate],
  );

  const rotateContiguousCells = useCallback(
    (
      cellIds: string[],
      center: BoundaryPoint,
      degrees: number,
      origins: Record<string, BoundaryPoint>,
      detachShared: boolean,
    ): Record<string, BoundaryPoint> => {
      let captured = origins;
      applyGestureMeshUpdate((mesh) => {
        if (cellIds.length === 0) {
          return null;
        }

        let working = mesh;
        if (detachShared && selectionSharesExternalVertices(working, cellIds)) {
          working = detachIsland(working, cellIds);
          captured = cellRingVertexOrigins(working, cellIds);
        }

        return rotateCellsFromOrigins(working, captured, center, degrees);
      });
      return captured;
    },
    [applyGestureMeshUpdate],
  );

  const mergeSelectedCells = useCallback(() => {
    if (!isPlotGridModeRef.current) {
      return;
    }

    const currentGrid = gridRef.current;
    if (
      currentGrid == null ||
      !currentGrid.isCommitted ||
      currentGrid.mesh == null ||
      !isMergeable(currentGrid.mesh, selectedCellIdsRef.current)
    ) {
      return;
    }

    const nextMesh = mergeCells(currentGrid.mesh, selectedCellIdsRef.current);
    if (nextMesh == null) {
      return;
    }

    const nextGrid: PlotGrid = {
      ...currentGrid,
      mesh: nextMesh,
    };

    logGridOperation("mergeSelectedCells", {
      after: summarizeGrid(nextGrid),
      before: summarizeGrid(currentGrid),
      selected: selectedCellIdsRef.current.length,
    });
    beginGridHistoryGesture();
    gridRef.current = nextGrid;
    setGrid(nextGrid);
    setSelectedCellIds([]);
    selectedCellIdsRef.current = [];
    endGridHistoryGesture();
  }, [beginGridHistoryGesture, endGridHistoryGesture]);

  const moveGridEdge = useCallback(
    (
      edge: GridEdgeRef,
      delta: { latitude: number; longitude: number },
      chainVertexIds?: string[],
    ) => {
      applyGestureMeshUpdate((mesh) =>
        chainVertexIds != null && chainVertexIds.length > 2
          ? moveEdgeChain(mesh, chainVertexIds, delta)
          : moveEdge(mesh, edge, delta),
      );
    },
    [applyGestureMeshUpdate],
  );

  const moveGridVertex = useCallback(
    (vertexId: string, point: BoundaryPoint) => {
      applyGestureMeshUpdate((mesh) => moveVertex(mesh, vertexId, point));
    },
    [applyGestureMeshUpdate],
  );

  const restoreGridHistorySnapshot = useCallback(
    (snapshot: GridHistorySnapshot) => {
      setGrid(snapshot.grid);
      gridRef.current = snapshot.grid;
      setSelectedCellIds(snapshot.selectedCellIds);
      selectedCellIdsRef.current = snapshot.selectedCellIds;
      setSelectedVertexId(null);
    },
    [],
  );

  const redoGrid = useCallback(() => {
    if (!isPlotGridModeRef.current) {
      return;
    }

    const currentGrid = gridRef.current;
    const history = gridHistoryRef.current;
    if (
      currentGrid == null ||
      !hasRecordableGridHistory(currentGrid) ||
      !canRedoGridHistory(history)
    ) {
      return;
    }

    const beforeMesh = currentGrid.mesh ?? null;
    const nextState = gridHistoryReducer(history, {
      snapshot: {
        grid: currentGrid,
        selectedCellIds: selectedCellIdsRef.current,
      },
      type: "redo",
    });
    if (nextState == null) {
      return;
    }

    gridHistoryRef.current = nextState;
    setGridHistory(nextState);
    const snapshot = nextState.entries[nextState.index];
    if (snapshot == null) {
      return;
    }
    logGridOperation("redo", {
      after: summarizeGrid(snapshot.grid),
      before: summarizeGrid(currentGrid),
      historyIndex: nextState.index,
      historySize: nextState.entries.length,
    });
    restoreGridHistorySnapshot(snapshot);
    syncOverlapToMeshChange(beforeMesh, snapshot.grid.mesh ?? null);
  }, [restoreGridHistorySnapshot, syncOverlapToMeshChange]);

  const resetToInitialPlacement = useCallback(
    (
      rows: number,
      columns: number,
      plotLengthMeters: number,
      plotWidthMeters: number,
      cellSeeds?: Record<string, GridCellSeed>,
    ) => {
      if (
        !Number.isInteger(rows) ||
        !Number.isInteger(columns) ||
        rows < 1 ||
        columns < 1 ||
        !Number.isFinite(plotLengthMeters) ||
        !Number.isFinite(plotWidthMeters) ||
        plotLengthMeters <= 0 ||
        plotWidthMeters <= 0
      ) {
        return;
      }

      const center = getFreeGridCenter();
      isPlotGridModeRef.current = true;
      setPlotGridMode(true);
      clearGridSelection();

      if (center == null) {
        logGridOperation("resetToInitialPlacement", {
          before: summarizeGrid(gridRef.current),
          placed: false,
        });
        gridRef.current = null;
        setGrid(null);
        setSavedGrid(null);
        endPlotGridSession();
        return;
      }

      const transform = createPlotSizedGridTransform(
        center,
        rows,
        columns,
        plotLengthMeters,
        plotWidthMeters,
        0,
      );
      const nextGrid = createPlotGrid(rows, columns, transform, {
        cellSeeds,
        plotLengthMeters,
        plotWidthMeters,
      });
      logGridOperation("resetToInitialPlacement", {
        after: summarizeLattice(nextGrid),
        before: summarizeGrid(gridRef.current),
      });
      gridRef.current = nextGrid;
      setGrid(nextGrid);
      setSavedGrid(null);
      clearGridHistory();
      seedGridHistoryFromCurrent();
    },
    [
      clearGridHistory,
      clearGridSelection,
      endPlotGridSession,
      getFreeGridCenter,
      seedGridHistoryFromCurrent,
    ],
  );

  const setGridDimensions = useCallback(
    (
      rows: number,
      columns: number,
      cellSeeds?: Record<string, GridCellSeed>,
    ) => {
      if (
        !isPlotGridModeRef.current ||
        !Number.isInteger(rows) ||
        !Number.isInteger(columns) ||
        rows < 1 ||
        columns < 1
      ) {
        return;
      }

      const currentGrid = gridRef.current;
      if (currentGrid?.isCommitted) {
        return;
      }

      logGridOperation("setGridDimensions", {
        before: summarizeLattice(currentGrid),
        requested: { columns, rows },
        seeds: Object.keys(cellSeeds ?? currentGrid?.cellSeeds ?? {}).length,
      });

      const plotLengthMeters =
        currentGrid?.plotLengthMeters ?? DEFAULT_PLOT_LENGTH_METERS;
      const plotWidthMeters =
        currentGrid?.plotWidthMeters ?? DEFAULT_PLOT_WIDTH_METERS;
      const center = currentGrid?.transform
        ? {
            latitude: currentGrid.transform.centerLatitude,
            longitude: currentGrid.transform.centerLongitude,
          }
        : getFreeGridCenter();
      if (center == null) {
        return;
      }

      const transform = createPlotSizedGridTransform(
        center,
        rows,
        columns,
        plotLengthMeters,
        plotWidthMeters,
        currentGrid?.transform?.rotationDegrees ?? 0,
      );
      const nextGrid = createPlotGrid(rows, columns, transform, {
        cellSeeds: cellSeeds ?? currentGrid?.cellSeeds,
        invertColumnLabels: currentGrid?.invertColumnLabels ?? false,
        invertRowLabels: currentGrid?.invertRowLabels ?? false,
        plotLengthMeters,
        plotWidthMeters,
      });
      gridRef.current = nextGrid;
      setGrid(nextGrid);
    },
    [getFreeGridCenter],
  );

  const setGridPlotSize = useCallback(
    (plotLengthMeters: number, plotWidthMeters: number) => {
      if (
        !isPlotGridModeRef.current ||
        !Number.isFinite(plotLengthMeters) ||
        !Number.isFinite(plotWidthMeters) ||
        plotLengthMeters <= 0 ||
        plotWidthMeters <= 0
      ) {
        return;
      }

      recordDiscreteLatticeEdit(() => {
        const currentGrid = gridRef.current;
        if (
          currentGrid == null ||
          currentGrid.isCommitted ||
          currentGrid.rows == null ||
          currentGrid.columns == null
        ) {
          return;
        }

        logGridOperation("setGridPlotSize", {
          before: summarizeLattice(currentGrid),
          requested: { plotLengthMeters, plotWidthMeters },
        });

        const center = currentGrid.transform
          ? {
              latitude: currentGrid.transform.centerLatitude,
              longitude: currentGrid.transform.centerLongitude,
            }
          : getFreeGridCenter();
        if (center == null) {
          return;
        }

        const transform = createPlotSizedGridTransform(
          center,
          currentGrid.rows,
          currentGrid.columns,
          plotLengthMeters,
          plotWidthMeters,
          currentGrid.transform?.rotationDegrees ?? 0,
        );
        const nextGrid = createPlotGrid(
          currentGrid.rows,
          currentGrid.columns,
          transform,
          {
            cellSeeds: currentGrid.cellSeeds,
            invertColumnLabels: currentGrid.invertColumnLabels,
            invertRowLabels: currentGrid.invertRowLabels,
            plotLengthMeters,
            plotWidthMeters,
          },
        );
        gridRef.current = nextGrid;
        setGrid(nextGrid);
      });
    },
    [getFreeGridCenter, recordDiscreteLatticeEdit],
  );

  const setGridLabelInversion = useCallback(
    (invertRowLabels: boolean, invertColumnLabels: boolean) => {
      if (!isPlotGridModeRef.current) {
        return;
      }

      recordDiscreteLatticeEdit(() => {
        const currentGrid = gridRef.current;
        if (currentGrid == null || currentGrid.isCommitted) {
          return;
        }

        if (
          currentGrid.invertRowLabels === invertRowLabels &&
          currentGrid.invertColumnLabels === invertColumnLabels
        ) {
          return;
        }

        logGridOperation("setGridLabelInversion", {
          before: {
            invertColumnLabels: currentGrid.invertColumnLabels,
            invertRowLabels: currentGrid.invertRowLabels,
          },
          requested: { invertColumnLabels, invertRowLabels },
        });

        const nextGrid: PlotGrid = {
          ...currentGrid,
          invertColumnLabels,
          invertRowLabels,
        };
        gridRef.current = nextGrid;
        setGrid(nextGrid);
      });
    },
    [recordDiscreteLatticeEdit],
  );

  const slideGridEdge = useCallback(
    (
      edge: GridEdgeRef,
      pointer: BoundaryPoint,
      chainVertexIds?: string[],
      frozenConstraint?: FrozenEdgeSlideConstraint | null,
    ): GridEdgeSlideResult => {
      const fallbackChain = chainVertexIds ?? [
        edge.startVertexId,
        edge.endVertexId,
      ];
      const mesh = gridRef.current?.mesh;
      if (mesh == null) {
        return { chainVertexIds: fallbackChain, edge };
      }

      if (chainVertexIds != null && chainVertexIds.length > 2) {
        const next = slideDetachedEdgeChain(
          mesh,
          chainVertexIds,
          pointer,
          edge,
          frozenConstraint ?? null,
        );
        applyGestureMeshUpdate(() => next.mesh);
        return { chainVertexIds: next.vertexIds, edge: next.edge };
      }

      const next = slideSingleEdge(
        mesh,
        edge,
        pointer,
        frozenConstraint ?? null,
      );
      applyGestureMeshUpdate(() => next.mesh);
      return {
        chainVertexIds: [next.edge.startVertexId, next.edge.endVertexId],
        edge: next.edge,
      };
    },
    [applyGestureMeshUpdate],
  );

  const finalizeGridEdgeSlide = useCallback(
    (edge: GridEdgeRef, chainVertexIds?: string[]) => {
      applyGestureMeshUpdate((mesh) => {
        if (chainVertexIds != null && chainVertexIds.length > 2) {
          const next = finalizeEdgeChainSlide(mesh, chainVertexIds);
          return next.mesh === mesh ? null : next.mesh;
        }
        const next = finalizeSingleEdgeSlide(mesh, edge);
        return next.mesh === mesh ? null : next.mesh;
      });
    },
    [applyGestureMeshUpdate],
  );

  const slideGridVertex = useCallback(
    (
      vertexId: string,
      pointer: BoundaryPoint,
      frozenConstraint?: FrozenVertexSlideConstraint | null,
    ) => {
      applyGestureMeshUpdate((mesh) =>
        slideVertex(mesh, vertexId, pointer, frozenConstraint ?? null),
      );
    },
    [applyGestureMeshUpdate],
  );

  const setGridSelectedCellIds = useCallback((cellIds: string[]) => {
    if (!isPlotGridModeRef.current) {
      return;
    }

    selectedCellIdsRef.current = cellIds;
    setSelectedCellIds(cellIds);
  }, []);

  const setGridSelectedVertexId = useCallback((vertexId: string | null) => {
    if (!isPlotGridModeRef.current) {
      return;
    }

    setSelectedVertexId(vertexId);
  }, []);

  const toggleCellSelection = useCallback((cellId: string) => {
    if (!isPlotGridModeRef.current) {
      return;
    }

    setSelectedCellIds((current) => {
      const next = current.includes(cellId)
        ? current.filter((id) => id !== cellId)
        : [...current, cellId];
      selectedCellIdsRef.current = next;
      return next;
    });
  }, []);

  const undoGrid = useCallback(() => {
    if (!isPlotGridModeRef.current) {
      return;
    }

    const currentGrid = gridRef.current;
    const history = gridHistoryRef.current;
    if (
      currentGrid == null ||
      !hasRecordableGridHistory(currentGrid) ||
      !canUndoGridHistory(history)
    ) {
      return;
    }

    const beforeMesh = currentGrid.mesh ?? null;
    const nextState = gridHistoryReducer(history, {
      snapshot: {
        grid: currentGrid,
        selectedCellIds: selectedCellIdsRef.current,
      },
      type: "undo",
    });
    if (nextState == null) {
      return;
    }

    gridHistoryRef.current = nextState;
    setGridHistory(nextState);
    const snapshot = nextState.entries[nextState.index];
    if (snapshot == null) {
      return;
    }
    logGridOperation("undo", {
      after: summarizeGrid(snapshot.grid),
      before: summarizeGrid(currentGrid),
      historyIndex: nextState.index,
      historySize: nextState.entries.length,
    });
    restoreGridHistorySnapshot(snapshot);
    syncOverlapToMeshChange(beforeMesh, snapshot.grid.mesh ?? null);
  }, [restoreGridHistorySnapshot, syncOverlapToMeshChange]);

  const updateGridTransform = useCallback((transform: GridTransform) => {
    if (!isPlotGridModeRef.current) {
      return;
    }

    const currentGrid = gridRef.current;
    if (
      currentGrid == null ||
      currentGrid.isCommitted ||
      currentGrid.rows == null ||
      currentGrid.columns == null
    ) {
      return;
    }

    const { plotLengthMeters, plotWidthMeters } = plotSizeFromGridTransform(
      transform,
      currentGrid.rows,
      currentGrid.columns,
    );
    const nextGrid = createPlotGrid(
      currentGrid.rows,
      currentGrid.columns,
      transform,
      {
        cellSeeds: currentGrid.cellSeeds,
        invertColumnLabels: currentGrid.invertColumnLabels,
        invertRowLabels: currentGrid.invertRowLabels,
        plotLengthMeters,
        plotWidthMeters,
      },
    );
    gridRef.current = nextGrid;
    setGrid(nextGrid);
  }, []);

  const selectGrower = useCallback((growerGeoId: number | null) => {
    setSelectedGrowerGeoId(growerGeoId);
    setSelectedSiteGeoId(null);
    setSelectedTrialId(null);
  }, []);

  const selectSite = useCallback((siteGeoId: number | null) => {
    setSelectedSiteGeoId(siteGeoId);
    setSelectedTrialId(null);
  }, []);

  const selectTrial = useCallback((trialId: number | null) => {
    setSelectedTrialId(trialId);
  }, []);

  return (
    <PlotMapContext
      value={{
        boundary: {
          acceptSaved: acceptSavedBoundary,
          addPoint: addBoundaryPoint,
          canRedo: canRedoBoundaryHistory(boundaryHistory),
          canUndo: canUndoBoundaryHistory(boundaryHistory),
          cancelDraft: cancelBoundaryDraft,
          clear: clearBoundary,
          close: closeBoundary,
          editingGrowerGeoId,
          editingSiteGeoId,
          endHistoryGesture: commitBoundaryHistoryGesture,
          enterGrowerBoundaryMode,
          enterSiteBoundaryMode,
          insertPoint: insertBoundaryPoint,
          isClosed: isBoundaryClosed,
          isDirty: isBoundaryDirty,
          isGrowerBoundaryMode,
          isSiteBoundaryMode,
          label: boundaryLabel,
          loadSaved: loadSavedBoundary,
          pauseGrowerBoundaryMode,
          points: boundaryPoints,
          redo: redoBoundary,
          removePoint: removeBoundaryPoint,
          setLabel: setBoundaryLabel,
          undo: undoBoundary,
          updatePoint: updateBoundaryPoint,
        },
        plot: {
          acceptSaved: acceptSavedGrid,
          beginHistoryGesture: beginGridHistoryGesture,
          canRedo: canRedoGridHistory(gridHistory),
          canUndo: canUndoGridHistory(gridHistory),
          cancelDraft: cancelGridDraft,
          clearSelection: clearGridSelection,
          commit: commitGrid,
          current: grid,
          deleteCell: deleteGridCell,
          deleteEdge: deleteGridEdge,
          deleteVertex: deleteGridVertex,
          detachAndTranslateSelection,
          endHistoryGesture: endGridHistoryGesture,
          enterPlotGridMode,
          finalizeEdgeSlide: finalizeGridEdgeSlide,
          growerGeoId: selectedGrowerGeoId,
          insertVertexOnEdge: insertGridVertexOnEdge,
          isDirty: isGridDirty,
          isPlotGridMode,
          loadSaved: loadSavedGrid,
          mergeSelectedCells,
          moveEdge: moveGridEdge,
          moveVertex: moveGridVertex,
          pausePlotGridMode,
          plotsOverlap: meshOverlap.plotPairKeys.size > 0,
          redo: redoGrid,
          resetToInitialPlacement,
          rotateContiguousCells,
          rowsOverlap: meshOverlap.rowPairKeys.size > 0,
          selectedCellIds,
          selectedVertexId,
          selectGrower,
          selectSite,
          selectTrial,
          setDimensions: setGridDimensions,
          setLabelInversion: setGridLabelInversion,
          setPlotSize: setGridPlotSize,
          setSelectedCellIds: setGridSelectedCellIds,
          setSelectedVertexId: setGridSelectedVertexId,
          siteGeoId: selectedSiteGeoId,
          slideEdge: slideGridEdge,
          slideVertex: slideGridVertex,
          toggleCellSelection,
          trialId: selectedTrialId,
          undo: undoGrid,
          updateTransform: updateGridTransform,
        },
      }}
    >
      {children}
    </PlotMapContext>
  );
};

export default PlotMapProvider;
